import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeftIcon, ArrowRightIcon, ChevronUpIcon, XIcon, PhoneIcon } from 'lucide-react';
import { FunnelProvider, STEPS, STEP_ESTIMATE, STEP_RESERVE, STEP_STATUS, useFunnel } from '../contexts/FunnelContext';
import { FunnelHeader } from '../components/funnel/FunnelHeader';
import { HomeLanding } from '../components/funnel/HomeLanding';
import { ProgressBar } from '../components/funnel/ProgressBar';
import { StepSize } from '../components/funnel/StepSize';
import { StepAddresses } from '../components/funnel/StepAddresses';
import { StepAccess } from '../components/funnel/StepAccess';
import { StepInventory } from '../components/funnel/StepInventory';
import { StepPacking } from '../components/funnel/StepPacking';
import { StepDate } from '../components/funnel/StepDate';
import { StepContact } from '../components/funnel/StepContact';
import { StepEstimate } from '../components/funnel/StepEstimate';
import { StepReserve } from '../components/funnel/StepReserve';
import { StepStatus } from '../components/funnel/StepStatus';
import { QuoteSummary } from '../components/funnel/QuoteSummary';
import { stepErrors, type StepErrors } from '../utils/validation';
import { hrs } from '../utils/format';
import { phones } from '../data/siteConfig';
import { SiteFooter } from '../components/SiteFooter';
import { firePageView } from '../utils/metaPixel';
const completionEvents: Record<number, string> = {
  1: 'AddressCompleted',
  2: 'AccessCompleted',
  3: 'InventoryCompleted'
};
function FunnelFlow() {
  const {
    step,
    goTo,
    data,
    t,
    track,
    quote,
    submitted,
    acceptEstimate
  } = useFunnel();
  const [attempted, setAttempted] = useState<Record<number, boolean>>({});
  const [sheet, setSheet] = useState(false);
  const errors: StepErrors = attempted[step] ? stepErrors(step, data) : {};
  const showNav = step <= STEP_ESTIMATE;
  const next = () => {
    const e = stepErrors(step, data);
    if (Object.keys(e).length) {
      setAttempted(a => ({
        ...a,
        [step]: true
      }));
      return;
    }
    if (completionEvents[step]) track(completionEvents[step], {
      step
    });
    if (step === STEP_ESTIMATE) acceptEstimate();
    goTo(step + 1);
  };
  const content = [<StepSize key="s" errors={errors} data-id="element-1159" data-mp="3igci95ye9fqc" />, <StepAddresses key="a" errors={errors} data-id="element-1160" data-mp="3djdowpmk1ct7" />, <StepAccess key="ac" data-id="element-1161" data-mp="3dkf496alslgq" />, <StepInventory key="i" errors={errors} data-id="element-1162" data-mp="2pu19v94jqaiz" />, <StepPacking key="p" errors={errors} data-id="element-1163" data-mp="2puujchuw3e4t" />, <StepDate key="d" errors={errors} data-id="element-1164" data-mp="2lsfyjs7nu4aw" />, <StepContact key="c" errors={errors} data-id="element-1165" data-mp="2lthb614bqyuq" />, <StepEstimate key="e" data-id="element-1166" data-mp="2lqd14n46h8w5" />, <StepReserve key="r" data-id="element-1167" data-mp="2zjkt4fw686ed" />, <StepStatus key="st" data-id="element-1168" data-mp="2zkw3wzo1aeez" />][step];
  return <>
      <ProgressBar data-id="element-1169" data-mp="2ut4eisgm6eqf" />
      <div className="mx-auto grid max-w-6xl gap-10 px-4 pb-32 pt-8 sm:px-6 lg:grid-cols-[1fr_320px] lg:pb-16 lg:pt-10" data-id="element-1170" data-mp="2usmbnczg529j">
        <main data-id="element-1171" data-mp="2728p21zmrvmc">
          <AnimatePresence mode="wait" initial={false} data-id="element-1172" data-mp="271yus27jz2au">
            <motion.div key={step} initial={{
            opacity: 0,
            x: 12
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -12
          }} transition={{
            duration: 0.18,
            ease: [0.23, 1, 0.32, 1]
          }} data-id="element-1173" data-mp="22506w1ehfrl3">
              {content}
            </motion.div>
          </AnimatePresence>

          {showNav && <div className="fixed inset-x-0 bottom-0 z-20 border-t border-ink-200 bg-white/95 px-4 py-3 backdrop-blur lg:static lg:mt-12 lg:border-0 lg:bg-transparent lg:p-0 lg:backdrop-blur-0" data-id="element-1174" data-mp="8lzrj3zte955">
              <div className="mx-auto flex max-w-6xl items-center gap-3" data-id="element-1175" data-mp="11aonyppxdqak">
                {step > 0 && <button type="button" onClick={() => goTo(step - 1)} className="flex h-14 items-center gap-2 rounded-xl border-2 border-ink-200 bg-white px-4 font-semibold text-ink transition-[border-color] duration-150 ease-out hover:border-ink" data-id="element-1176" data-mp="15cijkjl9k43v">
                    <ArrowLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-1177" data-mp="3c1oua0kmh3zo" />
                    <span className="hidden sm:inline" data-id="element-1178" data-mp="3h1tarqlj5ypw">{t('back')}</span>
                  </button>}
                {data.bedrooms && <button type="button" onClick={() => setSheet(true)} className="hidden h-14 flex-col items-start justify-center rounded-xl bg-ink-100 px-3 text-left min-[420px]:flex lg:hidden" aria-label={t('summary')} data-id="element-1179" data-mp="7wtnx8306r8">
                    <span className="flex items-center gap-1 text-[11px] font-medium text-ink-500" data-id="element-1180" data-mp="3ik1o16hnig4u">
                      {t('summary')} <ChevronUpIcon className="h-3 w-3" aria-hidden="true" data-id="element-1181" data-mp="j56ar7pxp85b" />
                    </span>
                    <span className="whitespace-nowrap text-sm font-bold text-ink" data-id="element-1182" data-mp="f2t7qr2knoc8">
                      {quote.crew.used} · {hrs(quote.serviceLow)}–{hrs(quote.serviceHigh)}h
                    </span>
                  </button>}
                <a href={phones.ai.href} aria-label={`${t('callKyle')} ${phones.ai.display}`} className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl border-2 border-ink-200 bg-white text-ink lg:hidden" data-id="element-1183" data-mp="56znelim2qjw">
                  <PhoneIcon className="h-5 w-5" aria-hidden="true" data-id="element-1184" data-mp="2ft0h7drlyoy0" />
                </a>
                <button type="button" onClick={next} className="flex h-14 flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-magenta px-5 text-[17px] font-extrabold text-white transition-[background-color,transform] duration-150 ease-out hover:bg-magenta-dark active:scale-[0.98] sm:text-[19px] lg:max-w-sm" data-id="element-1185" data-mp="2kqs7y4gio50m">
                  {step === STEP_ESTIMATE ? t('acceptContinue') : t('next')}
                  <ArrowRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-1186" data-mp="38gwdwih6ggus" />
                </button>
              </div>
            </div>}
          {step === STEP_RESERVE && <button type="button" onClick={() => goTo(step - 1)} className="mt-6 inline-flex min-h-[44px] items-center gap-2 font-semibold text-ink underline decoration-ink-300 underline-offset-4" data-id="element-1187" data-mp="1e2loa7srdiny">
              <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-1188" data-mp="1azfjj1gvj9ll" />
              {t('back')}
            </button>}
        </main>
        <aside className="hidden lg:block" data-id="element-1189" data-mp="162yt20m7cj5c">
          <div className="sticky top-24" data-id="element-1190" data-mp="1kyxzz7xksd8w">
            <QuoteSummary onEdit={submitted || step === STEP_STATUS ? undefined : goTo} data-id="element-1191" data-mp="1kzzmq9nyeo6g" />
          </div>
        </aside>
      </div>

      <AnimatePresence data-id="element-1192" data-mp="1g1obpvlxa7mn">
        {sheet && <motion.div className="fixed inset-0 z-40 flex items-end bg-ink/50 lg:hidden" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} onClick={() => setSheet(false)} data-id="element-1193" data-mp="1ty1mklvybas5">
            <motion.div role="dialog" aria-modal="true" aria-label={t('summary')} className="w-full p-3" initial={{
          y: 40,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} exit={{
          y: 40,
          opacity: 0
        }} transition={{
          duration: 0.25,
          ease: [0.23, 1, 0.32, 1]
        }} onClick={e => e.stopPropagation()} data-id="element-1194" data-mp="23o4m74xkvmzg">
              <div className="relative" data-id="element-1195" data-mp="2wbe1rv6d1hvn">
                <button type="button" onClick={() => setSheet(false)} aria-label={t('close')} className="absolute right-3 top-3 z-10 flex h-10 w-10 items-center justify-center rounded-lg text-ink hover:bg-ink-100" data-id="element-1196" data-mp="2wabavn91fffq">
                  <XIcon className="h-5 w-5" data-id="element-1197" data-mp="2n94v5ge7q8nj" />
                </button>
                <QuoteSummary onEdit={s => {
              setSheet(false);
              goTo(s);
            }} data-id="element-1198" data-mp="28ljvjwgjqrkd" />
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}
function FunnelShell() {
  const {
    started,
    lang
  } = useFunnel();
  return <div className="min-h-screen w-full bg-paper" data-id="element-1199" data-mp="23kcibhvmx06b">
      <FunnelHeader data-id="element-1200" data-mp="1po8ty57fzm1r" />
      {started ? <FunnelFlow data-id="element-1201" data-mp="1pp0p5ex4nhri" /> : <HomeLanding data-id="element-1202" data-mp="1um0wru9mkskq" />}
      <SiteFooter lang={lang} data-id="element-1203" data-mp="1umk28bi39ohv" />
    </div>;
}
export function FunnelPage({
  creativeKey
}: {
  creativeKey: string;
}) {
  useEffect(() => {
    firePageView();
  }, []);
  const urlKey = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('creative_key') : null;
  return <FunnelProvider creativeKey={urlKey ?? creativeKey} data-id="element-1204" data-mp="212u8p55cnlm0">
      <FunnelShell data-id="element-1205" data-mp="215ghf83z875t" />
    </FunnelProvider>;
}
export { STEPS };